// ---------------------------------------------------------------------------
// Applying a new build, on purpose.
//
// The service worker is registered with registerType 'prompt' and never calls
// skipWaiting, so a new build waits until the app is fully closed. That is
// deliberate -- reloading the page out from under someone mid-form loses the
// visit they are standing there typing -- but it also means a canvasser who
// keeps the app open all shift never receives a fix, however urgent.
//
// So the waiting build is surfaced instead of hidden, and applying it is the
// canvasser's choice. Nothing reloads on its own.
// ---------------------------------------------------------------------------

import { registerSW } from 'virtual:pwa-register';

type Listener = (ready: boolean) => void;

const listeners = new Set<Listener>();
let updateReady = false;
let apply: ((reload?: boolean) => Promise<void>) | null = null;

export function onUpdateReady(fn: Listener): () => void {
  listeners.add(fn);
  fn(updateReady);
  return () => listeners.delete(fn);
}

/** Activates the waiting worker and reloads. Only ever called from a tap. */
export async function applyUpdate(): Promise<void> {
  if (apply) await apply(true);
}

export function startUpdateWatch(): void {
  apply = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateReady = true;
      for (const fn of listeners) fn(true);
    },
  });
}
